import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useOrgFeatures } from '../hooks/useOrgFeatures';
import AccessCheckFailed from './AccessCheckFailed';

function OrgFeatureGate({ children, requiredFeature, fallbackPath, onRetry }) {
  const { hasFeature, loading, error } = useOrgFeatures();

  if (loading) {
    return (
      // Same quiet spinner as PermissionProtectedRoute: the stacked route guards
      // used to flash a different caption each in turn.
      <div role="status" className="flex h-screen items-center justify-center">
        <div aria-hidden="true" className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-teal-600 dark:border-gray-700 dark:border-t-teal-500" />
        <span className="sr-only">Verifying organization access...</span>
      </div>
    );
  }

  // A failed load (5xx, network error) leaves the feature list empty, which
  // looked exactly like "not entitled" and redirected. Unknown is not denied.
  if (error) return <AccessCheckFailed onRetry={onRetry} />;

  if (!hasFeature(requiredFeature)) {
    return <Navigate to={fallbackPath} replace />;
  }

  return children;
}

export default function OrgFeatureProtectedRoute({
  children,
  requiredFeature,
  fallbackPath = '/admin',
}) {
  // useOrgFeatures loads once per mount, so Retry remounts the gate to refetch.
  const [attempt, setAttempt] = useState(0);
  return (
    <OrgFeatureGate
      key={attempt}
      requiredFeature={requiredFeature}
      fallbackPath={fallbackPath}
      onRetry={() => setAttempt((n) => n + 1)}
    >
      {children}
    </OrgFeatureGate>
  );
}
